import { ref, ComputedRef, Ref, ComponentPublicInstance } from 'vue';
import { ColumnOrderState } from '@tanstack/vue-table';
import {
  Sortable,
  Draggable,
  SortableStartEvent,
  SortableSortedEvent,
  SortableSortEvent,
  MirrorCreatedEvent,
  MirrorMoveEvent,
  MirrorDestroyEvent,
  DragOutEvent,
} from '@shopify/draggable';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridStyleOption,
  GridColumnOrderingOption,
  ColumnVisibilityInfo,
} from '../../modernGrid.types';
import { getIsDefaultColumn } from '../../modernGrid.utils';

const DRAG_DISTANCE_PX = 5;
const PRESS_HOLD_DELAY_MS = 100;

/**
 * Transform이 적용된 조상 요소의 viewport 기준 offset을 계산
 * SlideOutWindow와 같이 transform이 적용된 컨테이너 내에서 미러 위치를 보정하는 데 사용
 */
const getTransformContainingBlockOffset = (element: HTMLElement): { x: number; y: number } => {
  let current: HTMLElement | null = element.parentElement;

  while (current && current !== document.body) {
    const style = getComputedStyle(current);
    if (style.transform && style.transform !== 'none') {
      const rect = current.getBoundingClientRect();
      return { x: rect.left, y: rect.top };
    }
    current = current.parentElement;
  }

  return { x: 0, y: 0 };
};

export const useColumnOrdering = <RowDataType extends GridRawRowDataType>({
  table,
  leftColumnIds,
  rightColumnIds,
  columnOrderState,
  columnOrderingInfo,
  columnSettingList,
  styleInfo,
  expandingColumn,
}: {
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  leftColumnIds: ComputedRef<string[]>;
  rightColumnIds: ComputedRef<string[]>;
  columnOrderState: Ref<ColumnOrderState>;
  columnOrderingInfo: ComputedRef<GridColumnOrderingOption>;
  columnSettingList: Ref<ColumnVisibilityInfo[]>;
  styleInfo: ComputedRef<GridStyleOption>;
  expandingColumn: Ref<string>;
}) => {
  const isDragging = ref<boolean>(false);
  const isOverNonDroppable = ref<boolean>(false);
  const columnRefs = ref<HTMLElement[]>([]);

  let sortableInstance: Sortable | null = null;
  let mirrorEl: HTMLElement | null = null;
  let transformOffset = { x: 0, y: 0 };
  let cursorOffset = { x: 0, y: 0 };
  let pressHoldTimerId: ReturnType<typeof setTimeout> | null = null;
  let pressHoldTarget: HTMLElement | null = null;

  // 마지막 컬럼 드래깅 중인 경우 해당 컬럼의 ID
  const draggedLastColumnId = ref<string | null>(null);

  const setColumnRef = (el: Element | ComponentPublicInstance | null, index: number) => {
    if (el && el instanceof HTMLElement) {
      columnRefs.value[index] = el;
    }
  };

  const changeColumnOrder = ({ oldIndex, newIndex }: SortableSortedEvent) => {
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    // DOM에서 실제 보이는 컬럼들의 ID 순서를 가져옴
    const visibleColumns = table.value?.getVisibleLeafColumns() || [];
    const visibleColumnIds = visibleColumns.map((col) => col.id);

    if (oldIndex >= visibleColumnIds.length || newIndex >= visibleColumnIds.length) {
      return;
    }

    // DOM 인덱스를 columnOrderState 인덱스로 매핑
    const draggedColumnId = visibleColumnIds[oldIndex];
    const targetColumnId = visibleColumnIds[newIndex];

    const actualOldIndex = columnOrderState.value.indexOf(draggedColumnId);
    const actualNewIndex = columnOrderState.value.indexOf(targetColumnId);

    if (actualOldIndex === -1 || actualNewIndex === -1) {
      return;
    }

    // columnOrderState 업데이트
    const newColumnOrder = [...columnOrderState.value];
    const [movedItem] = newColumnOrder.splice(actualOldIndex, 1);
    newColumnOrder.splice(actualNewIndex, 0, movedItem);

    columnOrderState.value = newColumnOrder;
    table.value?.setColumnOrder(newColumnOrder);

    const currentSettings = columnSettingList.value.slice();
    if (currentSettings.length) {
      const settingsMap = new Map(currentSettings.map((item) => [item.text, item]));
      const reorderedSettings = newColumnOrder
        .map((id) => settingsMap.get(id))
        .filter((item): item is ColumnVisibilityInfo => !!item);

      if (reorderedSettings.length) {
        const remainingSettings = currentSettings.filter(
          (item) => !reorderedSettings.includes(item),
        );
        columnSettingList.value = [...reorderedSettings, ...remainingSettings];
      }
    }

    // 마지막 컬럼을 드래그한 경우 ID 저장 (렌더링 문제 해결을 위해)
    if (!draggedLastColumnId.value) {
      const lastColumn = table.value?.getColumn(draggedColumnId);
      if (lastColumn?.getIsLastColumn()) {
        draggedLastColumnId.value = draggedColumnId;
      }
    }
  };

  const setDraggingState = (val: boolean): void => {
    isDragging.value = val;
  };

  const checkDraggable = (evt: SortableSortEvent) => {
    if ('over' in evt && evt.over) {
      const overId = (evt.over as any).dataset.columnId || '';
      const isNonDroppable =
        leftColumnIds.value.includes(overId) ||
        rightColumnIds.value.includes(overId) ||
        getIsDefaultColumn(overId) ||
        (expandingColumn.value && overId === expandingColumn.value);

      isOverNonDroppable.value = Boolean(isNonDroppable);

      if (isNonDroppable) {
        evt.cancel();
      }
    } else {
      isOverNonDroppable.value = false;
    }
  };

  const onMirrorCreated = (evt: MirrorCreatedEvent) => {
    const { mirror, source, sensorEvent } = evt;
    if (mirror) {
      mirrorEl = mirror;
      transformOffset = getTransformContainingBlockOffset(source);

      // source 요소 내에서 커서의 상대 위치 계산
      const sourceRect = source.getBoundingClientRect();
      cursorOffset = {
        x: sensorEvent.clientX - sourceRect.left,
        y: sensorEvent.clientY - sourceRect.top,
      };

      const bgColor = styleInfo.value.header?.backgroundColor ?? 'var(--grid-header-bg-color)';
      mirror.style.backgroundColor = bgColor;
    }
  };

  const onMirrorMove = (evt: MirrorMoveEvent) => {
    if (!mirrorEl || (transformOffset.x === 0 && transformOffset.y === 0)) {
      return; // Transform 없으면 기본 동작
    }

    const { sensorEvent } = evt;
    const x = sensorEvent.clientX - cursorOffset.x - transformOffset.x;
    const y = sensorEvent.clientY - cursorOffset.y - transformOffset.y;

    mirrorEl.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    evt.cancel();
  };

  const onMirrorDestroy = (_evt: MirrorDestroyEvent) => {
    mirrorEl = null;
    transformOffset = { x: 0, y: 0 };
    cursorOffset = { x: 0, y: 0 };
  };

  const onStartDragging = (evt: SortableStartEvent) => {
    // 컬럼 리사이즈 중이면 드래그 취소
    if (table.value?.getState().columnSizingInfo?.isResizingColumn) {
      evt.cancel();
      return;
    }

    // source 컬럼이 드래그 불가능한 컬럼인지 확인
    const sourceEl = evt.dragEvent.source as HTMLElement;
    const sourceColumnId = sourceEl?.dataset?.columnId || '';

    if (
      expandingColumn.value === sourceColumnId ||
      leftColumnIds.value.includes(sourceColumnId) ||
      rightColumnIds.value.includes(sourceColumnId) ||
      getIsDefaultColumn(sourceColumnId)
    ) {
      evt.cancel();
      return;
    }

    setDraggingState(true);
  };

  const onStopDragging = () => {
    setDraggingState(false);
    isOverNonDroppable.value = false;
  };

  const onDragOut = (_evt: DragOutEvent) => {
    isOverNonDroppable.value = false;
  };

  const clearPressHoldState = () => {
    if (pressHoldTimerId !== null) {
      clearTimeout(pressHoldTimerId);
      pressHoldTimerId = null;
    }
    if (pressHoldTarget) {
      pressHoldTarget.classList.remove('is-press-holding');
      pressHoldTarget = null;
    }
  };

  const onHeaderRowMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return;

    const target = event.target as HTMLElement;
    const cell = target.closest?.('.modern-grid-header__cell.not-orderable') as HTMLElement | null;
    if (!cell) return;

    clearPressHoldState();

    pressHoldTarget = cell;
    pressHoldTimerId = setTimeout(() => {
      pressHoldTarget?.classList.add('is-press-holding');
    }, PRESS_HOLD_DELAY_MS);

    document.addEventListener('mouseup', clearPressHoldState, { once: true });
  };

  const destroyColumnDraggable = () => {
    columnRefs.value.forEach((el) => {
      el.removeEventListener('mousedown', onHeaderRowMouseDown);
    });
    clearPressHoldState();
    document.removeEventListener('mouseup', clearPressHoldState);

    if (sortableInstance) {
      // 이벤트 리스너 제거 (메모리 누수 방지)
      sortableInstance.off('mirror:created', onMirrorCreated);
      sortableInstance.off('mirror:move', onMirrorMove);
      sortableInstance.off('mirror:destroy', onMirrorDestroy);
      sortableInstance.off('sortable:start', onStartDragging);
      sortableInstance.off('sortable:sort', checkDraggable);
      sortableInstance.off('sortable:sorted', changeColumnOrder);
      sortableInstance.off('sortable:stop', onStopDragging);
      sortableInstance.off('drag:out', onDragOut);
      sortableInstance.destroy();
      sortableInstance = null;
    }

    mirrorEl = null;
    transformOffset = { x: 0, y: 0 };
    cursorOffset = { x: 0, y: 0 };
    draggedLastColumnId.value = null;
  };

  const initColumnDraggable = () => {
    destroyColumnDraggable();

    // 초기화 조건 확인
    if (
      !columnOrderingInfo.value.use ||
      !columnRefs.value.length ||
      (table.value?.getHeaderGroups() || []).length > 1
    ) {
      return;
    }

    sortableInstance = new Sortable(columnRefs.value, {
      draggable: '.modern-grid-header__cell',
      distance: DRAG_DISTANCE_PX,
      classes: {
        'draggable:over': ['is-drop-over'],
        mirror: ['is-dragging-mirror'],
      } as any,
      mirror: {
        constrainDimensions: true, // 원본 셀 크기 유지
      },
    });

    // 헤더 컬럼 드래그 시 자동 스크롤 비활성화 (Scrollable 플러그인 제거)
    sortableInstance.removePlugin(Draggable.Plugins.Scrollable);

    // 이벤트 리스너 등록
    sortableInstance
      .on('mirror:created', onMirrorCreated)
      .on('mirror:move', onMirrorMove)
      .on('mirror:destroy', onMirrorDestroy)
      .on('sortable:start', onStartDragging)
      .on('sortable:sort', checkDraggable)
      .on('sortable:sorted', changeColumnOrder)
      .on('sortable:stop', onStopDragging)
      .on('drag:out', onDragOut);

    // not-orderable 컬럼 press-hold 커서 피드백 리스너 등록
    columnRefs.value.forEach((el) => {
      el.addEventListener('mousedown', onHeaderRowMouseDown);
    });
  };

  return {
    isDragging,
    isOverNonDroppable,
    draggedLastColumnId,
    setColumnRef,
    initColumnDraggable,
    destroyColumnDraggable,
  };
};
